/**
 * client/src/lib/webauthn/local-credential.ts — offer the passkey that is
 * actually on THIS device, so the OS prompt opens instead of the device chooser.
 *
 * THE SYMPTOM, AND WHY IT IS WINDOWS-ONLY
 * ---------------------------------------
 * `/api/auth/webauthn/authenticate/begin` offers EVERY active credential (server
 * `helpers.toAllowCredentials`). An account with a Windows Hello passkey AND an
 * iPhone passkey therefore hands Chrome-on-Windows a list it cannot resolve
 * locally — credential ids are opaque, so the browser cannot tell which entry
 * is the one sitting in the TPM two inches away.
 *
 * Its only correct move is to offer every transport the list mentions. Windows
 * then opens its own chooser and, to populate it, powers the Bluetooth adapter
 * for a hybrid (caBLE) advertisement and enumerates USB HID. That spin-up — not
 * the Hello prompt itself — is the dead air before the prompt. iOS shows no such
 * gap on the same options because it resolves the local credential first and
 * defers hybrid until asked: same list, different arbitration, which is why the
 * delay is platform-shaped while the server response is not (ledger #78 named
 * this candidate and left it untouched; this is that candidate).
 *
 * THE FIX
 * -------
 * Remember which credential answered on this device, then offer that one alone
 * next time. One `internal`-transport entry gives the browser an unambiguous
 * answer, so there is no chooser to populate and no radio to warm. The memory
 * is written ONLY from a completed assertion whose `authenticatorAttachment`
 * was `"platform"` — the browser itself saying the credential answered from
 * here; it lives in `localStorage`, per browser-profile per machine, never
 * synced; and it is CONFIRMED on every use, narrowing only when the remembered
 * id is still in the server's offered list, so a revoked, deleted or
 * wrong-account credential simply fails that check.
 *
 * NOT THE ONLY PREFERENCE, AND NOT THE FIRST. A memory of what is LOCAL can
 * only ever name a local passkey, so alone it routes an account holding both a
 * Hello passkey and an iPhone passkey to Hello forever. `chosen-credential.ts`
 * records what the person actually CHOSE, at any attachment, and takes
 * precedence here — read it for the lockout this ordering exists to end.
 *
 * WHY THIS CANNOT WEAKEN AUTHENTICATION
 * ------------------------------------
 * `allowCredentials` is UX routing metadata, never a security input — the same
 * posture documented on server `helpers.toAllowCredentials` and on
 * `webauthn-credential-pin.ts`. `completeAuthentication` keys on the
 * credential-id lookup, account ownership, revocation, origin, challenge, UV
 * and counter regardless of what was offered, so narrowing can only make a
 * ceremony fail closed, never admit a credential the server would reject. No
 * AAL, quorum, or step-up gate reads this module.
 *
 * NOT `pinAllowCredentials` (webauthn-credential-pin.ts) — that is a
 * CORRECTNESS requirement (a record sealed under one credential's PRF output;
 * a missing pin THROWS before prompting). This is a PREFERENCE: any of the
 * account's credentials would authenticate fine. It never throws and never
 * blocks — a miss degrades to the untouched list.
 *
 * ESCAPE HATCH: any narrowed ceremony that does not complete releases its
 * narrowing, so a passkey deleted in Windows settings — or simply the wrong one
 * for what the person is doing — costs one attempt and not a lockout. See THE
 * RELEASE RULE on the seam below.
 *
 * THE FIRST SIGN-IN, WHICH THE MEMORY CANNOT COVER (ledger #91). A device that
 * has never completed a ceremony pays the full chooser cost. REGISTRATION
 * TEACHES IT (`startRegistration` reports the same attachment for a credential
 * just created HERE, so auth-entry register and Add-a-key record it), and THE
 * HINT STANDS ALONE — `hints: ["client-device"]` is advisory, not a filter, so
 * it is set whenever this device is KNOWN to have a platform authenticator and
 * one offered credential could live in it, memory or no memory. On an
 * UNnarrowed list every other option stays in the dialog.
 *
 * HONEST SCOPE: this changes WHICH credential is asked for and HOW FAST the
 * prompt opens. It proves possession + freshness exactly as before — never
 * liveness, never that a particular human is present (CLAUDE.md §Friction Rule
 * clause 2).
 */

import { startAuthentication } from "@simplewebauthn/browser";
import type {
  AuthenticationResponseJSON,
  PublicKeyCredentialRequestOptionsJSON,
} from "@simplewebauthn/browser";
import {
  isInternalCapable,
  isInternalOnly,
  platformAuthenticatorKnownAvailable,
  primePlatformAuthenticatorAvailability,
} from "./platform-authenticator-availability";
import { forgetChosenCredential, readChosenCredential } from "./chosen-credential";

/**
 * Per browser-profile, per machine. `localStorage` is deliberate: `sessionStorage`
 * would forget between tabs and re-pay the delay, and a cookie would ship this to
 * the server, which has no use for it and should not be asked to trust it.
 */
const STORAGE_KEY = "strenn:localPlatformCredentialId";

/** Every access is wrapped — Safari private mode and locked-down profiles throw
 *  on `localStorage`, and a sign-in must never fail over a latency hint. */
function safeRead(): string | null {
  try {
    const value = window.localStorage.getItem(STORAGE_KEY);
    return value && value.length > 0 ? value : null;
  } catch {
    return null;
  }
}

/** The credential id last known to have answered from this device, if any. */
export function readLocalCredential(): string | null {
  return safeRead();
}

/**
 * The two fields this module reads off a finished ceremony. Deliberately
 * structural rather than `AuthenticationResponseJSON`: a REGISTRATION response
 * carries the same two, for a credential the browser has just created on this
 * very device, and that is the strongest locality evidence there is — see
 * THE FIRST SIGN-IN above. One recorder, both ceremonies.
 */
export interface LocalCredentialEvidence {
  id?:                     string;
  authenticatorAttachment?: string | null;
}

/**
 * Record the credential that just answered (or was just created) — but ONLY
 * when the browser reported `authenticatorAttachment === "platform"`.
 *
 * That field is the whole basis for the claim "this credential is on this
 * device". Without it we would be inferring locality from stored transports,
 * which are captured at registration on a possibly different machine. An absent
 * or cross-platform attachment leaves any existing memory untouched rather than
 * overwriting it, so one security-key sign-in does not cost the next platform
 * sign-in its fast path.
 */
export function rememberLocalCredential(response: LocalCredentialEvidence): void {
  if (response.authenticatorAttachment !== "platform") return;
  if (!response.id) return;
  try {
    window.localStorage.setItem(STORAGE_KEY, response.id);
  } catch {
    /* A browser that refuses storage simply keeps today's speed. */
  }
}

/** Drop the memory so the next ceremony offers the full list (escape hatch). */
export function forgetLocalCredential(): void {
  try {
    window.localStorage.removeItem(STORAGE_KEY);
  } catch {
    /* nothing to do — a memory we cannot clear is one we could not read either */
  }
}

export interface PreferredAuthenticationOptions {
  /** The options to pass to `startAuthentication`. */
  optionsJSON: PublicKeyCredentialRequestOptionsJSON;
  /**
   * True iff the offered list was actually narrowed to a preferred credential.
   * A failure on an UNnarrowed list says nothing about either preference and
   * must not clear one.
   */
  narrowed: boolean;
  /** Which preference did the narrowing, so a failure releases that one. */
  source: "chosen" | "local" | null;
}

/**
 * Narrow `allowCredentials` to this device's known-local credential, and tell
 * the browser so.
 *
 * Two independent effects, both no-ops when their precondition is absent:
 *
 *  1. NARROWING. When the remembered id is in the offered list, the list becomes
 *     exactly that entry, with its transports rewritten to `["internal"]`. The
 *     rewrite matters as much as the narrowing: a Chrome-synced passkey reports
 *     `["internal","hybrid"]` at registration, and a single entry still
 *     mentioning `hybrid` re-opens the cross-device path the narrowing exists to
 *     close. We are entitled to the rewrite because the memory was written from
 *     a `"platform"` attachment on this very device.
 *
 *  2. HINTS. WebAuthn L3 `hints: ["client-device"]` asks the browser to try the
 *     built-in authenticator before building a cross-device chooser; it is
 *     advisory, never a filter. Set on EITHER ground: the resulting list is
 *     entirely internal-only (the narrowed case), or this device is KNOWN to
 *     have a platform authenticator and one offered credential advertises
 *     `internal` (ledger #91). Both are withheld when nothing offered could be
 *     internal, so a security-key-only account is never sent to Hello.
 *
 * An absent or empty `allowCredentials` is the discoverable-credential flow —
 * there is nothing to narrow and nothing we know, so it is returned untouched.
 */
export function preferLocalCredential(
  optionsJSON: PublicKeyCredentialRequestOptionsJSON,
): PreferredAuthenticationOptions {
  const offered = optionsJSON.allowCredentials;
  if (!Array.isArray(offered) || offered.length === 0) {
    return { optionsJSON, narrowed: false, source: null };
  }

  // The credential the person CHOSE outranks the one that merely happens to be
  // local — see NOT THE ONLY PREFERENCE above. Both are confirmed against
  // the list the server just offered, so neither can conjure a credential.
  const chosen = readChosenCredential();
  const chosenMatch = chosen ? offered.find((c) => c.id === chosen.id) : undefined;
  const remembered = chosenMatch ? null : safeRead();
  const localMatch = remembered ? offered.find((c) => c.id === remembered) : undefined;
  const match = chosenMatch ?? localMatch;
  const source = chosenMatch ? "chosen" as const : localMatch ? "local" as const : null;

  // Rewriting transports to `internal` is licensed ONLY by evidence that this
  // credential answered from THIS device: the local memory is written from a
  // "platform" attachment by definition, and a chosen credential carries the
  // attachment it reported. For anything else the server's transports are kept
  // verbatim — dropping `hybrid` is precisely what put a phone passkey out of
  // reach, and this module must not do it again.
  const isHere = source === "local" || chosen?.attachment === "platform";
  // An already-internal-only entry passes through BY REFERENCE — `narrowed`
  // below measures change structurally, so a no-op rewrite must not copy.
  const allowCredentials = match
    ? [isHere && !isInternalOnly(match) ? { ...match, transports: ["internal" as const] } : match]
    : offered;

  const localOnly = allowCredentials.every(isInternalOnly);
  const localPossible = platformAuthenticatorKnownAvailable()
    && allowCredentials.some(isInternalCapable);
  // A chosen credential that may live on a phone is never hinted to the
  // built-in authenticator: the hint would point the browser away from it.
  const hintable = source !== "chosen" || isHere;

  return {
    optionsJSON: {
      ...optionsJSON,
      allowCredentials,
      ...(hintable && (localOnly || localPossible) ? { hints: ["client-device" as const] } : {}),
    },
    // "We removed OPTIONS", NOT merely "a preference matched" — an option
    // being an entry OR a route, measured as "does the returned list differ
    // from the offered one" (untouched entries pass through by reference).
    // A single already-internal-only entry runs the same ceremony with or
    // without us, so a failure there must not release the preference. But a
    // single entry whose `hybrid` route the rewrite dropped — including one a
    // caller pinned via `pinAllowCredentials`, since pins keep the server's
    // transports — is NOT the same ceremony: if this device's copy is gone
    // while a synced copy survives, the rewrite is what made it unreachable,
    // and a failure must release or the retry dead-ends (ledger #108).
    narrowed: source !== null && !(
      allowCredentials.length === offered.length
      && allowCredentials.every((entry, i) => entry === offered[i])
    ),
    source,
  };
}

/**
 * Run an assertion local-credential-first: prefer, assert, remember.
 *
 * THE SEAM. Every ceremony that wants the fast prompt calls this instead of
 * `startAuthentication` directly, so the sequence exists once. Three call sites
 * open-coding it is how they drift — and they did: the release rule below is
 * subtle enough that two of the original three got it wrong.
 *
 * THE RELEASE RULE. Any failure of a NARROWED ceremony releases whichever
 * preference did the narrowing; a failure on an unnarrowed list releases
 * nothing, because it says nothing about a credential that was never the one
 * offered.
 *
 * A user cancel is INCLUDED, reversing the original rule. A narrowed list holds
 * one passkey and no route to the others — no chooser, no QR — so dismissing is
 * the only signal that means "not that one", and reading it as "same key next
 * time" is what turned a latency preference into a lockout (2026-08-20: an
 * iPhone passkey unreachable in a browser profile by ANY sequence of clicks).
 * Releasing costs one chooser-delayed ceremony, after which a completed
 * platform assertion re-learns the fast path.
 *
 * Errors propagate unchanged, so every caller keeps its own cancel/error copy
 * and its own state machine. This function adds no UI and swallows nothing.
 */
export async function startLocalFirstAuthentication(
  optionsJSON: PublicKeyCredentialRequestOptionsJSON,
): Promise<AuthenticationResponseJSON> {
  const prepared = preferLocalCredential(optionsJSON);
  // Backstop for ceremonies that never mount the sign-in form: fire-and-forget,
  // so THIS call is never delayed by it and the next one reads a settled
  // answer. `/auth` primes on mount and does not rely on this.
  primePlatformAuthenticatorAvailability();

  let response: AuthenticationResponseJSON;
  try {
    response = await startAuthentication({ optionsJSON: prepared.optionsJSON });
  } catch (error) {
    if (prepared.narrowed) {
      if (prepared.source === "chosen") forgetChosenCredential();
      else forgetLocalCredential();
    }
    throw error;
  }

  rememberLocalCredential(response);
  return response;
}
